import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { Button, ButtonSize } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { MenuDropdown } from '../../../shared/components/menu-dropdown/menu-dropdown';
import type { ISelectBoxItem } from '../../../shared/components/menu-dropdown/select-box/select-box';
import { Tab } from '../../../shared/components/tab/tab';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { useContextNavigate } from '../../../shared/hooks/use-context-navigate';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import type { IAccountLayoutSettings } from '../../layouts/account/account.interface';

import styles from './account-menu.module.scss';

const style = createStyleHelper(styles, 'account-menu');

export interface IAccountMenuProps {
    className?: string;
    currentPage: string;
    userAccountSections: IAccountLayoutSettings[];
}

export const AccountMenu: FunctionComponent<IAccountMenuProps> = ({
    className,
    currentPage,
    userAccountSections,
}) => {
    const intl = useIntl();
    const auth = useAuth();
    const navigate = useContextNavigate();
    const isFullDisplay = useDesktop();
    const onSelectMenuItem = useCallback(
        (item: ISelectBoxItem) => {
            if (!item.href) {
                return;
            }

            navigate(item.href);
        },
        [navigate],
    );

    return (
        <div className={createClassName([className, style()])}>
            {isFullDisplay ? (
                <div className={style('account-info')}>
                    <div className={style('action-section')}>
                        <h1 className={style('full-name')}>
                            {`${
                                auth.user?.profile.given_name
                            } ${auth.user?.profile.family_name?.charAt(0)}`}
                        </h1>
                        <div className={style('button-section')}>
                            <Button
                                className={style('button-item')}
                                size={ButtonSize.MEDIUM}
                                href="/cashier/deposit"
                            >
                                <FormattedMessage id="account.deposit" defaultMessage="DEPOSIT" />
                            </Button>
                            <Button
                                className={style('button-item')}
                                size={ButtonSize.MEDIUM}
                                hollow
                                light
                                href="/cashier/withdrawal"
                            >
                                <FormattedMessage id="account.withdraw" defaultMessage="WITHDRAW" />
                            </Button>
                        </div>
                    </div>
                    <div className={style('navigation-tabs')}>
                        {userAccountSections.map((item, i) => {
                            return (
                                <Tab
                                    className={style('tab')}
                                    key={item.id}
                                    href={item.href}
                                    thick
                                    align={i === 0 ? 'left' : 'center'}
                                >
                                    <FormattedMessage
                                        id={item.translationKey}
                                        defaultMessage={item.defaultDisplayName}
                                    />
                                </Tab>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <MenuDropdown
                    onSelect={onSelectMenuItem}
                    selectedItemId={currentPage}
                    items={userAccountSections.map((section) => ({
                        value: section?.id,
                        name: intl.formatMessage({
                            id: section.translationKey,
                            defaultMessage: section.defaultDisplayName,
                        }),
                        href: section.href,
                        id: section?.id,
                    }))}
                />
            )}
        </div>
    );
};
